import React, { useState, useRef, useEffect } from "react";
import Portal from "../../components/Portal";
import { useParams, useNavigate } from "react-router-dom";
import { approvalService, publishedService } from "../../services/firestoreService";
import { auth } from "../../firebase";
import { serverTimestamp } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  FiArrowLeft,
  FiRotateCcw,
  FiRotateCw,
  FiChevronDown,
  FiBold,
  FiItalic,
  FiUnderline,
  FiMinus,
  FiCode,
  FiLink,
  FiImage,
  FiVolume2,
  FiVideo,
  FiUpload,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiX,
  FiEye,
  FiCheck,
  FiClock
} from "react-icons/fi";
import "./staffcss/contentdashboard.css";

const AdminContentRevise = () => {
  const { approvalId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState("");
  const [debouncedContent, setDebouncedContent] = useState("");
  const [characterCount, setCharacterCount] = useState(0);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [selectedStyle, setSelectedStyle] = useState("Normal text");
  const [embedBar, setEmbedBar] = useState(null);
  const [alignment, setAlignment] = useState("left");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [publishCategory, setPublishCategory] = useState("");
  const [minorEligible, setMinorEligible] = useState(false);
  const [isEditSocialOpen, setIsEditSocialOpen] = useState(false);
  const [socialTitle, setSocialTitle] = useState("");
  const [socialDescription, setSocialDescription] = useState("");
  const [socialImageUrl, setSocialImageUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [approvalData, setApprovalData] = useState(null);
  const [actualDocumentId, setActualDocumentId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isProposing, setIsProposing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [originalContent, setOriginalContent] = useState("");
  const [originalTitle, setOriginalTitle] = useState("");
  const [originalSubtitle, setOriginalSubtitle] = useState("");
  const [originalCategory, setOriginalCategory] = useState("");
  const [originalMinorEligible, setOriginalMinorEligible] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const editorRef = useRef(null);
  const cursorRestoreRef = useRef(null);
  const contentUpdateSourceRef = useRef(null);
  const importFileInputRef = useRef(null);
  const socialImageInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const historyTimerRef = useRef(null);

  const PUBLISH_CATEGORIES = [
    "Menstrual Wellness and Hygiene",
    "Contraception",
    "Fertility Basics",
    "STI Awareness",
    "Mental Health in Reproductive Care",
    "Male Reproductive Health",
    "Pregnancy Prevention",
    "General Reproductive Wellness",
    "Community Outreach Programs"
  ];

  const stripHtmlToText = (html) => {
    try {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
    } catch {
      return "";
    }
  };

  const calculateCharacterCount = (html) => {
    return stripHtmlToText(html).length;
  };

  const openPublishModal = () => {
    const bodyText = stripHtmlToText(debouncedContent);
    setSocialTitle((title || "").trim());
    setSocialDescription(
      ((subtitle || "").trim() || bodyText).slice(0, 160)
    );
    setIsPublishOpen(true);
  };

  const submitForApproval = async () => {
    try {
      setIsPublishing(true);

      if (!title.trim()) {
        alert("Please enter a title before publishing.");
        setIsPublishing(false);
        return;
      }
      if (!debouncedContent.trim() || debouncedContent === "<br>") {
        alert("Please add content before publishing.");
        setIsPublishing(false);
        return;
      }
      if (!publishCategory) {
        alert("Please select a category before publishing.");
        setIsPublishing(false);
        return;
      }

      const authUser = auth.currentUser;
      if (!authUser) {
        setIsPublishing(false);
        return;
      }

      if (!actualDocumentId) {
        alert('Unable to publish: Document ID not found');
        setIsPublishing(false);
        return;
      }

      // First update the approval with the revised content
      await approvalService.updateApproval(actualDocumentId, {
        title: title.trim(),
        subtitle: subtitle.trim(),
        category: publishCategory,
        bodyHtml: debouncedContent,
        socialTitle: socialTitle.trim(),
        socialDescription: socialDescription.trim(),
        socialImageUrl: socialImageUrl.trim(),
        minorEligible,
        updatedAt: serverTimestamp(),
      });

      // Then approve the content
      await approvalService.updateApprovalStatus(actualDocumentId, "Approved", {
        approvedAt: serverTimestamp(),
        approvedBy: "Admin"
      });

      closePublishModal();
      setShowSuccessModal(true);

    } catch (err) {
      const msg = err.message || "Publishing failed. Please try again.";
      alert(msg);
      setIsPublishing(false);
    }
  };

  const closePublishModal = () => {
    setIsPublishOpen(false);
    setIsEditSocialOpen(false);
  };

  const openEditSocial = () => {
    setIsPublishOpen(false);
    setIsEditSocialOpen(true);
  };

  const closeEditSocial = () => {
    setIsEditSocialOpen(false);
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setShowUnsavedModal(true);
    } else {
      navigate(`/admin/content-review/${approvalId}`);
    }
  };

  const handleLeaveUnsaved = () => {
    setShowUnsavedModal(false);
    navigate(`/admin/content-review/${approvalId}`);
  };

  const handleContinueEditing = () => {
    setShowUnsavedModal(false);
  };

  const handlePickSocialImage = (e) => {
    const file = e.target.files[0];
    if (!file.type?.startsWith("image/")) return;
    
    const maxSizeInBytes = 500 * 1024;
    if (file.size > maxSizeInBytes) {
      alert(`Image is too large. Please choose an image smaller than 500KB.`);
      e.target.value = "";
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = String(reader.result || "");
      const estimatedSizeInBytes = Math.round(base64String.length * 0.75);
      if (estimatedSizeInBytes > 1024 * 1024) {
        alert(`Image is still too large after encoding. Please choose a smaller image.`);
        e.target.value = "";
        return;
      }
      setSocialImageUrl(base64String);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const getCaretCharacterOffsetWithin = (element) => {
    if (!element || typeof element.nodeType !== "number") return { start: 0, end: 0 };
    const selection = window.getSelection();
    if (!selection.rangeCount) return { start: 0, end: 0 };
    const range = selection.getRangeAt(0);
    try {
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      const end = preCaretRange.toString().length;
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      const start = preCaretRange.toString().length;
      return { start, end };
    } catch (err) {
      return { start: 0, end: 0 };
    }
  };

  const setCaretPosition = (element, startOffset, endOffset) => {
    if (!element || typeof element.nodeType !== "number") return;
    const selection = window.getSelection();
    const range = document.createRange();
    let charCount = 0;
    let startNode = null, startOffsetInNode = 0, endNode = null, endOffsetInNode = 0;

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const nextCount = charCount + node.length;
        if (startNode === null && startOffset <= nextCount) {
          startNode = node;
          startOffsetInNode = startOffset - charCount;
        }
        if (endNode === null && endOffset <= nextCount) {
          endNode = node;
          endOffsetInNode = endOffset - charCount;
        }
        charCount = nextCount;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          walk(node.childNodes[i]);
          if (startNode !== null && endNode !== null) return;
        }
      }
    };
    walk(element);
    if (startNode === null) {
      startNode = element;
      startOffsetInNode = element.childNodes.length;
    }
    if (endNode === null) {
      endNode = element;
      endOffsetInNode = element.childNodes.length;
    }
    try {
      range.setStart(startNode, startOffsetInNode);
      range.setEnd(endNode, endOffsetInNode);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
      try {
        range.selectNodeContents(element);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (e2) {}
    }
  };

  const saveToHistory = () => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(debouncedContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleContentChange = (e, options) => {
    const el = e.target;
    const editor = editorRef.current;
    let newContent = editor && editor.innerHTML !== undefined ? editor.innerHTML : "";
    let count = calculateCharacterCount(newContent);

    if (count > 5000) {
      let truncated = "";
      let tempLength = 0;
      const div = document.createElement("div");
      div.innerHTML = newContent;

      const truncateNode = (node) => {
        if (tempLength >= 5000) return;
        if (node.nodeType === Node.TEXT_NODE) {
          const remaining = 5000 - tempLength;
          if (node.length <= remaining) {
            truncated += node.textContent;
            tempLength += node.length;
          } else {
            truncated += node.textContent.substring(0, remaining);
            tempLength = 5000;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tag = node.tagName.toLowerCase();
          truncated += `<${tag}>`;
          for (let i = 0; i < node.childNodes.length; i++) {
            truncateNode(node.childNodes[i]);
            if (tempLength >= 5000) break;
          }
          if (tempLength < 5000) {
            truncated += `</${tag}>`;
          }
        }
      };

      for (let i = 0; i < div.childNodes.length; i++) {
        truncateNode(div.childNodes[i]);
        if (tempLength >= 5000) break;
      }

      newContent = truncated;
      count = 5000;

      if (editor) {
        contentUpdateSourceRef.current = "programmatic";
        editor.innerHTML = newContent;
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    const isUserInput = el && typeof el.nodeType === "number" && editor && editor.contains(el);
    if (isUserInput && editor) {
      contentUpdateSourceRef.current = "user";
      editor.style.direction = "ltr";
      editor.style.unicodeBidi = "embed";
    } else if (!options?.preserveCursor) {
      cursorRestoreRef.current = null;
      contentUpdateSourceRef.current = "programmatic";
    }

    setContent(newContent);
    setCharacterCount(count);
    
    // Debounce history saves to reduce frequency (2 second delay)
    if (historyTimerRef.current) {
      clearTimeout(historyTimerRef.current);
    }
    
    historyTimerRef.current = setTimeout(() => {
      saveToHistory();
    }, 2000);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      contentUpdateSourceRef.current = "programmatic";
      setContent(history[historyIndex - 1]);
      setDebouncedContent(history[historyIndex - 1]);
      setCharacterCount(calculateCharacterCount(history[historyIndex - 1]));
      if (editorRef.current) {
        editorRef.current.innerHTML = history[historyIndex - 1];
      }
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      contentUpdateSourceRef.current = "programmatic";
      setContent(history[historyIndex + 1]);
      setDebouncedContent(history[historyIndex + 1]);
      setCharacterCount(calculateCharacterCount(history[historyIndex + 1]));
      if (editorRef.current) {
        editorRef.current.innerHTML = history[historyIndex + 1];
      }
    }
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const execCommand = (command, value = null) => {
    const editor = editorRef.current;
    if (!editor) return;
    focusEditor();
    document.execCommand(command, false, value);
    contentUpdateSourceRef.current = "programmatic";
    cursorRestoreRef.current = getCaretCharacterOffsetWithin(editor);
    handleContentChange({ target: { innerHTML: editor.innerHTML } }, { preserveCursor: true });
  };

  const insertHTMLAtCursor = (html) => {
    const editor = editorRef.current;
    if (!editor) return;
    focusEditor();
    document.execCommand("insertHTML", false, html);
    contentUpdateSourceRef.current = "programmatic";
    cursorRestoreRef.current = getCaretCharacterOffsetWithin(editor);
    handleContentChange({ target: { innerHTML: editor.innerHTML } }, { preserveCursor: true });
  };

  const embedRemoveButton = '<span class="cc-embed-remove" role="button" tabindex="-1" title="Remove" aria-label="Remove">&times;</span>';

  const handleEditorClick = (e) => {
    const editor = editorRef.current;
    if (!editor) return;
    const removeBtn = e.target.closest?.(".cc-embed-remove");
    if (!removeBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const embed = removeBtn.closest?.(".cc-embed");
    if (embed && editor.contains(embed)) {
      try {
        const sel = window.getSelection();
        const range = document.createRange();
        const prev = embed.previousSibling;
        if (prev) {
          range.setStartAfter(prev);
          range.collapse(true);
        } else {
          range.setStart(editor, 0);
          range.collapse(true);
        }
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (err) {}
      embed.remove();
      contentUpdateSourceRef.current = "programmatic";
      cursorRestoreRef.current = getCaretCharacterOffsetWithin(editor);
      handleContentChange({ target: { innerHTML: editor.innerHTML } }, { preserveCursor: true });
    }
  };

  const handleImportFiles = (e) => {
    const files = e.target.files;
    if (!files || !files.length || !editorRef.current) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const type = file.type || "";
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        if (type.startsWith("image/")) {
          insertHTMLAtCursor(
            `<figure class="cc-embed cc-embed-image" contenteditable="false">${embedRemoveButton}<img src="${dataUrl}" alt="${file.name || ""}" /><figcaption contenteditable="true">Caption</figcaption></figure>`
          );
        } else if (type.startsWith("audio/")) {
          insertHTMLAtCursor(
            `<div class="cc-embed cc-embed-audio" contenteditable="false">${embedRemoveButton}<audio controls src="${dataUrl}">Your browser does not support audio.</audio></div>`
          );
        } else if (type.startsWith("video/")) {
          insertHTMLAtCursor(
            `<div class="cc-embed cc-embed-video" contenteditable="false">${embedRemoveButton}<video controls src="${dataUrl}" style="max-width:100%;height:auto;">Your browser does not support video.</video></div>`
          );
        }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const openEmbedBar = (type) => {
    let cursorOffset = null;
    try {
      const editor = editorRef.current;
      const sel = window.getSelection();
      if (editor && sel.rangeCount && sel.anchorNode && editor.contains(sel.anchorNode)) {
        cursorOffset = getCaretCharacterOffsetWithin(editor);
      }
    } catch (e) {}
    setEmbedBar({ type, url: "", text: "", cursorOffset });
  };

  const closeEmbedBar = () => {
    setEmbedBar(null);
  };

  const handleAlignment = (align) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const value = { left: "left", center: "center", right: "right", justify: "justify" }[align];
    if (!value) return;
    const cmd = { left: "justifyLeft", center: "justifyCenter", right: "justifyRight", justify: "justifyFull" }[align];
    document.execCommand(cmd, false, null);
    try {
      const sel = window.getSelection();
      if (sel.rangeCount && sel.anchorNode && editor.contains(sel.anchorNode)) {
        let node = sel.getRangeAt(0).commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
        const block = node.closest ? node.closest("p, h1, h2, h3, h4, h5, h6, div") : null;
        if (block && block !== editor && editor.contains(block)) {
          block.style.textAlign = value;
        }
      }
    } catch (e) {}
    setAlignment(align);
    const ed = editorRef.current;
    if (ed) {
      contentUpdateSourceRef.current = "programmatic";
      cursorRestoreRef.current = getCaretCharacterOffsetWithin(ed);
      handleContentChange({ target: { innerHTML: ed.innerHTML } }, { preserveCursor: true });
    }
  };

  const insertEmbed = () => {
    if (!embedBar || !editorRef.current) return;
    const { type, url, text, cursorOffset } = embedBar;
    const trimmedUrl = (url || "").trim();
    if (!trimmedUrl && type !== "comment") return;

    const editor = editorRef.current;
    if (cursorOffset != null) {
      const len = editor.textContent?.length ?? 0;
      setCaretPosition(editor, Math.min(cursorOffset.start, len), Math.min(cursorOffset.end, len));
    }
    focusEditor();

    switch (type) {
      case "link":
        if (trimmedUrl) {
          const linkText = (text || trimmedUrl).trim() || trimmedUrl;
          insertHTMLAtCursor(
            `<div class="cc-embed cc-embed-link" contenteditable="false">${embedRemoveButton}<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a><span class="cc-embed-link-url">${trimmedUrl}</span></div>`
          );
        }
        break;
      case "image":
        insertHTMLAtCursor(
          `<figure class="cc-embed cc-embed-image" contenteditable="false">${embedRemoveButton}<img src="${trimmedUrl}" alt="" /><figcaption contenteditable="true">Caption</figcaption></figure>`
        );
        break;
      case "audio":
        insertHTMLAtCursor(
          `<div class="cc-embed cc-embed-audio" contenteditable="false">${embedRemoveButton}<audio controls src="${trimmedUrl}">Your browser does not support audio.</audio></div>`
        );
        break;
      case "video":
        insertHTMLAtCursor(
          `<div class="cc-embed cc-embed-video" contenteditable="false">${embedRemoveButton}<video controls src="${trimmedUrl}" style="max-width:100%;height:auto;">Your browser does not support video.</video></div>`
        );
        break;
      default:
        break;
    }
    closeEmbedBar();
  };

  const handleStyleChange = (style) => {
    focusEditor();
    setSelectedStyle(style);
    switch(style) {
      case "Normal text":
        execCommand("formatBlock", "<p>");
        break;
      case "Heading 1":
        execCommand("formatBlock", "<h1>");
        break;
      case "Heading 2":
        execCommand("formatBlock", "<h2>");
        break;
      case "Heading 3":
        execCommand("formatBlock", "<h3>");
        break;
      case "Heading 4":
        execCommand("formatBlock", "<h4>");
        break;
      case "Heading 5":
        execCommand("formatBlock", "<h5>");
        break;
      case "Heading 6":
        execCommand("formatBlock", "<h6>");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fetchApprovalData();
  }, [approvalId]);

  const fetchApprovalData = async () => {
    try {
      setLoading(true);
      
      let data = await approvalService.getApprovalByContentId(approvalId);
      
      if (!data) {
        data = await approvalService.getApprovalById(approvalId);
      }
      
      if (!data) {
        data = await publishedService.getPublishedByContentId(approvalId);
      }
      
      if (data) {
        setApprovalData(data);
        setActualDocumentId(data.id); // Store the actual document ID
        setTitle(data.title || "");
        setSubtitle(data.subtitle || "");
        contentUpdateSourceRef.current = "programmatic";
        setContent(data.bodyHtml || "");
        setDebouncedContent(data.bodyHtml || "");
        setPublishCategory(data.category || "");
        setSocialTitle(data.socialTitle || data.title || "");
        setSocialDescription(data.socialDescription || "");
        setSocialImageUrl(data.socialImageUrl || "");
        setMinorEligible(data.minorEligible);
        setCharacterCount(calculateCharacterCount(data.bodyHtml || ""));

        // Store original values for change detection
        setOriginalContent(data.bodyHtml || "");
        setOriginalTitle(data.title || "");
        setOriginalSubtitle(data.subtitle || "");
        setOriginalCategory(data.category || "");
        setOriginalMinorEligible(data.minorEligible || false);
      }
    } catch (error) {
      alert("Failed to load content for revision.");
    } finally {
      setLoading(false);
    }
  };

  // Debounce content updates to reduce frequency of effect execution
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedContent(content);
      setCharacterCount(calculateCharacterCount(content));
    }, 150); // 150ms debounce delay

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [content]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (historyTimerRef.current) {
        clearTimeout(historyTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const isProgrammatic = contentUpdateSourceRef.current === "programmatic";
    const pendingCursor = cursorRestoreRef.current;

    if (isProgrammatic) {
      editor.innerHTML = debouncedContent || "Start writing...";
    } else if (debouncedContent === "" && (!editor.innerHTML || editor.innerHTML === "Start writing..." || editor.innerHTML === "<br>")) {
      editor.innerHTML = "Start writing...";
    }

    if (isProgrammatic && pendingCursor != null) {
      cursorRestoreRef.current = null;
      const { start, end } = pendingCursor;
      const len = editor.textContent?.length ?? 0;
      requestAnimationFrame(() => {
        const el = editorRef.current;
        if (el && typeof el.nodeType === "number" && document.contains(el)) {
          setCaretPosition(el, Math.min(start, len), Math.min(end, len));
        }
      });
    }
    
    contentUpdateSourceRef.current = null;
  }, [debouncedContent]);

  // Detect changes from original content
  useEffect(() => {
    const contentChanged = debouncedContent !== originalContent;
    const titleChanged = title !== originalTitle;
    const subtitleChanged = subtitle !== originalSubtitle;
    const categoryChanged = publishCategory !== originalCategory;
    const minorEligibleChanged = minorEligible !== originalMinorEligible;

    setHasChanges(contentChanged || titleChanged || subtitleChanged || categoryChanged || minorEligibleChanged);
  }, [debouncedContent, title, subtitle, publishCategory, minorEligible, originalContent, originalTitle, originalSubtitle, originalCategory, originalMinorEligible]);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      const handleFocus = () => {
        editor.style.direction = 'ltr';
        editor.style.unicodeBidi = 'embed';
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        const range = selection.getRangeAt(0);
        const startOffset = range.startOffset;
        const startContainer = range.startContainer;
        if (!startContainer || typeof startContainer.nodeType !== "number") return;

        setTimeout(() => {
          try {
            const newRange = document.createRange();
            if (startContainer.nodeType === Node.TEXT_NODE && document.contains(startContainer)) {
              newRange.setStart(startContainer, Math.min(startOffset, startContainer.length));
              newRange.collapse(true);
            } else if (document.contains(editor)) {
              newRange.selectNodeContents(editor);
              newRange.collapse(false);
            } else {
              return;
            }
            selection.removeAllRanges();
            selection.addRange(newRange);
          } catch (e) {
            try {
              if (document.contains(editor)) {
                const fallbackRange = document.createRange();
                fallbackRange.selectNodeContents(editor);
                fallbackRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(fallbackRange);
              }
            } catch (e2) {}
          }
        }, 0);
      };
      
      const handleInput = () => {
        editor.style.direction = 'ltr';
        editor.style.unicodeBidi = 'embed';
      };

      const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Backspace' || e.key === 'Delete') {
          setTimeout(() => {
            editor.style.direction = 'ltr';
            editor.style.unicodeBidi = 'embed';
          }, 0);
        }
        
        // Handle Tab key for indentation
        if (e.key === 'Tab') {
          e.preventDefault();
          if (e.shiftKey) {
            // Decrease indent - use outdent command
            document.execCommand('outdent', false, null);
          } else {
            // Increase indent - insert 4 spaces
            document.execCommand('insertText', false, '      ');
          }
          contentUpdateSourceRef.current = "programmatic";
          cursorRestoreRef.current = getCaretCharacterOffsetWithin(editor);
          handleContentChange({ target: { innerHTML: editor.innerHTML } }, { preserveCursor: true });
        }
      };

      const handleMouseUp = () => {
        setTimeout(() => {
          editor.style.direction = 'ltr';
          editor.style.unicodeBidi = 'embed';
        }, 0);
      };

      editor.addEventListener('focus', handleFocus);
      editor.addEventListener('input', handleInput);
      editor.addEventListener('keydown', handleKeyDown);
      editor.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        editor.removeEventListener('focus', handleFocus);
        editor.removeEventListener('input', handleInput);
        editor.removeEventListener('keydown', handleKeyDown);
        editor.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, []);

  if (loading) {
    return (
      <div className="cc-add-content-container">
        <style>{`
          .admin-aura-skeleton {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 4px;
          }
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          .admin-aura-skeleton-text {
            height: 16px;
            margin-bottom: 8px;
          }
          .admin-aura-skeleton-title {
            height: 32px;
            width: 60%;
            margin-bottom: 16px;
          }
          .admin-aura-skeleton-input {
            height: 40px;
            width: 100%;
          }
          .admin-aura-skeleton-editor {
            height: 300px;
            width: 100%;
          }
        `}</style>
        {/* Top Header Bar with System Branding (Refined) */}
        <header className="cc-top-header" style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px'
        }}>
          <div className="cc-header-left" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>Revising Content</span>
            <div className="admin-aura-skeleton" style={{ width: '160px', height: '36px', borderRadius: '8px' }} />
          </div>
          <div className="cc-header-right" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px'
          }}>
            <div className="admin-aura-skeleton" style={{ width: '280px', height: '40px', borderRadius: '10px' }} />
          </div>
        </header>

        {/* Editor Area */}
        <div className="cc-editor-area" style={{ 
          padding: '24px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)'
        }}>
          <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '50px', marginBottom: '16px' }} />
          <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '40px', marginBottom: '20px' }} />
          <div className="admin-aura-skeleton admin-aura-skeleton-editor" />
        </div>
      </div>
    );
  }

  return (
    <div className="cc-add-content-container">
      {/* Top Header Bar with System Branding (Refined) */}
      <header className="cc-top-header" style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
        padding: '16px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px'
      }}>
        <div className="cc-header-left" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="cc-back-btn"
            onClick={handleBackClick}
            style={{
              background: '#3F62B5',
              color: 'white',
              borderRadius: '10px',
              border: 'none',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(63, 98, 181, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#29499D';
              e.target.style.boxShadow = '0 6px 16px rgba(63, 98, 181, 0.4)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#3F62B5';
              e.target.style.boxShadow = '0 4px 12px rgba(63, 98, 181, 0.3)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <FiArrowLeft style={{ fontSize: '18px' }} />
          </button>
          <div className="cc-saved-status" style={{
            cursor: 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.06)',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            <div className="cc-status-dot saved" style={{
              background: '#34d399',
              width: '10px',
              height: '10px',
              borderRadius: '50%'
            }}></div>
            <span style={{
              color: '#e2e8f0',
              fontWeight: '600',
              fontSize: '14px'
            }}>Revising Content</span>
          </div>
          <div style={{
            cursor: 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 16px',
            background: 'rgba(255,255,255,0.06)',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            <span style={{
              color: characterCount >= 5000 ? '#ef4444' : (characterCount >= 1400 ? '#f59e0b' : '#e2e8f0'),
              fontWeight: '600',
              fontSize: '14px'
            }}>{characterCount} / 5000 characters</span>
          </div>
        </div>

        <div className="cc-header-right" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <select
            className="cc-publish-select"
            value={publishCategory}
            onChange={(e) => setPublishCategory(e.target.value)}
            style={{
              width: '280px',
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '10px',
              padding: '10px 14px',
              fontSize: '14px'
            }}
          >
            <option value="" style={{ background: '#0f172a', color: '#fff' }}>Select one category</option>
            {PUBLISH_CATEGORIES.map((c) => (
              <option key={c} value={c} style={{ background: '#0f172a', color: '#fff' }}>
                {c}
              </option>
            ))}
          </select>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="cc-preview-btn"
              type="button"
              onClick={() => {
                setPreviewDevice("desktop");
                setIsPreviewOpen(true);
              }}
              style={{
                background: 'rgba(255,255,255,0.12)',
                color: '#fff',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.18)',
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                e.target.style.borderColor = 'transparent';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.12)';
                e.target.style.borderColor = 'rgba(255,255,255,0.18)';
                e.target.style.color = '#fff';
              }}
            >
              Preview
            </button>
            <button
              className="cc-preview-btn"
              type="button"
              onClick={() => {
                setIsEditSocialOpen(true);
              }}
              style={{
                background: 'rgba(255,255,255,0.12)',
                color: '#fff',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.18)',
                padding: '10px 18px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                e.target.style.borderColor = 'transparent';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.12)';
                e.target.style.borderColor = 'rgba(255,255,255,0.18)';
                e.target.style.color = '#fff';
              }}
            >
              Social Preview
            </button>
          </div>

          <button
            className="cc-continue-btn"
            type="button"
            onClick={openPublishModal}
            disabled={!hasChanges}
            style={{
              background: !hasChanges ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.12)',
              color: !hasChanges ? 'rgba(255,255,255,0.4)' : '#fff',
              borderRadius: '10px',
              border: !hasChanges ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.18)',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              cursor: !hasChanges ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (hasChanges) {
                e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                e.target.style.borderColor = 'transparent';
                e.target.style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => {
              if (hasChanges) {
                e.target.style.background = 'rgba(255,255,255,0.12)';
                e.target.style.borderColor = 'rgba(255,255,255,0.18)';
                e.target.style.color = '#fff';
              }
            }}
          >
            Publish
          </button>
        </div>
      </header>

      {/* Toolbar */}
      <div className="cc-toolbar-header">
        <div className="cc-history-buttons">
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={handleUndo} disabled={historyIndex <= 0}>
            <FiRotateCcw />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={handleRedo} disabled={historyIndex >= history.length - 1}>
            <FiRotateCw />
          </button>
        </div>
        
        <div className="cc-editor-toolbar">
          {/* Style Dropdown */}
          <div className="cc-dropdown">
            <button className="cc-dropdown-btn" onMouseDown={(e) => e.preventDefault()}>
              {selectedStyle} <FiChevronDown size={12} />
            </button>
            <div className="cc-dropdown-menu">
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Normal text")}>Normal text</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 1")}>Heading 1</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 2")}>Heading 2</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 3")}>Heading 3</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 4")}>Heading 4</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 5")}>Heading 5</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 6")}>Heading 6</div>
            </div>
          </div>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Text Formatting */}
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('bold')}>
            <FiBold />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('italic')}>
            <FiItalic />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('underline')}>
            <FiUnderline />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('strikeThrough')}>
            <FiMinus />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('formatBlock', '<pre>')}>
            <FiCode />
          </button>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Media and Links */}
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('link')}
            title="Insert link"
          >
            <FiLink />
          </button>
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('image')}
            title="Insert image"
          >
            <FiImage />
          </button>
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('audio')}
            title="Insert audio"
          >
            <FiVolume2 />
          </button>
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('video')}
            title="Insert video"
          >
            <FiVideo />
          </button>
          <input
            ref={importFileInputRef}
            type="file"
            accept="image/*,audio/*,video/*"
            multiple
            className="cc-import-file-input"
            onChange={handleImportFiles}
            aria-label="Import image, audio, or video from device"
          />
          <button
            type="button"
            className="cc-tool-btn cc-import-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => importFileInputRef.current?.click()}
            title="Import image, audio, or video from device"
          >
            <FiUpload /> Import
          </button>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Lists */}
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('insertUnorderedList')} title="Bullet list">
            <FiList />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('insertOrderedList')} title="Numbered list">
            <FiList style={{ transform: 'rotate(90deg)' }} />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('indent')} title="Indent">
            <FiAlignLeft />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('outdent')} title="Decrease indent">
            <FiAlignLeft style={{ transform: 'scaleX(-1)' }} />
          </button>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Alignment */}
          <div className="cc-dropdown">
            <button className="cc-dropdown-btn" title="Text alignment" onMouseDown={(e) => e.preventDefault()}>
              Align <FiChevronDown size={12} />
            </button>
            <div className="cc-dropdown-menu">
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("left")}><FiAlignLeft /> Left</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("center")}><FiAlignCenter /> Center</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("right")}><FiAlignRight /> Right</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("justify")}><FiAlignJustify /> Justify</div>
            </div>
          </div>

          <div className="cc-toolbar-divider"></div>

          {/* Minor Eligible Checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px' }}>
            <input
              type="checkbox"
              id="minor-eligible-checkbox"
              checked={minorEligible === true}
              onChange={(e) => setMinorEligible(e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <label htmlFor="minor-eligible-checkbox" style={{ cursor: 'pointer', fontSize: '14px', color: '#fff', userSelect: 'none' }}>
              Minor Eligible Content
            </label>
          </div>

        </div>
      </div>

      {/* Embed bar */}
      {embedBar && (
        <div className="cc-embed-bar">
          {embedBar.type === "link" && (
            <>
              <input
                type="url"
                className="cc-embed-input"
                placeholder="URL"
                value={embedBar.url}
                onChange={(e) => setEmbedBar((b) => ({ ...b, url: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && insertEmbed()}
              />
              <input
                type="text"
                className="cc-embed-input"
                placeholder="Link text (optional)"
                value={embedBar.text}
                onChange={(e) => setEmbedBar((b) => ({ ...b, text: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && insertEmbed()}
              />
            </>
          )}
          {(embedBar.type === "image" || embedBar.type === "audio" || embedBar.type === "video") && (
            <input
              type="url"
              className="cc-embed-input cc-embed-input-wide"
              placeholder={`Enter ${embedBar.type} URL`}
              value={embedBar.url}
              onChange={(e) => setEmbedBar((b) => ({ ...b, url: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && insertEmbed()}
            />
          )}
          <div className="cc-embed-bar-actions">
            <button type="button" className="cc-btn cc-embed-insert" onClick={insertEmbed}>
              Insert
            </button>
            <button type="button" className="cc-btn cc-embed-cancel" onClick={closeEmbedBar}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="cc-editor-content">
        
        <input
          type="text"
          className="cc-title"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Content title"
        />
        
        <input
          type="text"
          className="cc-subtitle"
          placeholder="Add a subtitle..."
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          aria-label="Content subtitle"
        />

        <div
          ref={(el) => {
            editorRef.current = el;
            if (el && !el.innerHTML) {
              el.innerHTML = debouncedContent === "" ? "Start writing..." : debouncedContent;
            }
          }}
          className="cc-text-editor"
          contentEditable="true"
          suppressContentEditableWarning={true}
          onFocus={() => {
            if (debouncedContent === "") {
              contentUpdateSourceRef.current = "programmatic";
              setContent("<br>");
              setDebouncedContent("<br>");
            }
          }}
          onClick={handleEditorClick}
          onInput={handleContentChange}
          onKeyDown={(e) => {
            if (e.key === 'Tab') {
              e.preventDefault();
              if (e.shiftKey) {
                document.execCommand('outdent', false, null);
              } else {
                document.execCommand('insertText', false, '    ');
              }
              contentUpdateSourceRef.current = "programmatic";
              cursorRestoreRef.current = getCaretCharacterOffsetWithin(editorRef.current);
              handleContentChange({ target: { innerHTML: editorRef.current.innerHTML } }, { preserveCursor: true });
            }
          }}
          style={{ direction: 'ltr', unicodeBidi: 'embed' }}
        />
      </div>

      {/* Live Preview Modal with Branding */}
      {isPreviewOpen && (
        <Portal>
          <div className="cc-preview-overlay" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
            <div className="cc-preview-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <header className="cc-preview-header" style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                color: '#fff',
                padding: '24px 32px'
              }}>
                <div>
                  <div className="cc-edit-social-title" style={{ color: '#fff' }}>Preview</div>
                  <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                    See how your content will appear on different devices
                  </div>
                </div>
                <button
                  type="button"
                  className="cc-edit-social-close"
                  onClick={() => setIsPreviewOpen(false)}
                  aria-label="Close preview"
                  title="Close"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    borderRadius: '10px'
                  }}
                >
                  <FiX />
                </button>
              </header>

              <div style={{
                padding: '16px 32px',
                background: 'rgba(15, 23, 42, 0.5)',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div className="cc-preview-tabs" style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className={previewDevice === "mobile" ? "active" : ""}
                    onClick={() => setPreviewDevice("mobile")}
                    style={{
                      color: '#fff',
                      background: previewDevice === "mobile" ? 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)' : 'transparent',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Mobile
                  </button>
                  <button
                    type="button"
                    className={previewDevice === "desktop" ? "active" : ""}
                    onClick={() => setPreviewDevice("desktop")}
                    style={{
                      color: '#fff',
                      background: previewDevice === "desktop" ? 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)' : 'transparent',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Desktop
                  </button>
                </div>
              </div>

              <div className="cc-preview-body">
                {previewDevice === "mobile" && (
                  <div className="cc-preview-mobile-frame">
                    <div className="cc-preview-mobile-notch" />
                    <div className="cc-preview-mobile-content">
                      <h1>{title || "Title"}</h1>
                      {subtitle && <h2>{subtitle}</h2>}
                      <div
                        className="cc-preview-mobile-body"
                        dangerouslySetInnerHTML={{
                          __html: debouncedContent && debouncedContent !== "<br>" ? debouncedContent : "Start writing..."
                        }}
                      />
                    </div>
                  </div>
                )}

                {previewDevice === "desktop" && (
                  <div className="cc-preview-desktop-frame">
                    <div className="cc-preview-desktop-toolbar" />
                    <div className="cc-preview-desktop-content">
                      <h1>{title || "Title"}</h1>
                      {subtitle && <h2>{subtitle}</h2>}
                      <div
                        className="cc-preview-desktop-body"
                        dangerouslySetInnerHTML={{
                          __html: debouncedContent && debouncedContent !== "<br>" ? debouncedContent : "Start writing..."
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Publish Modal with Branding */}
      {isPublishOpen && (
        <Portal>
          <div className="cc-publish-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
            <div className="cc-publish-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <header className="cc-publish-header" style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                color: '#fff',
                padding: '24px 32px'
              }}>
                <div>
                  <div className="cc-publish-title" style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>Publish</div>
                  <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                    Review and publish your content changes
                  </div>
                </div>
                <button
                  type="button"
                  className="cc-publish-close"
                  onClick={closePublishModal}
                  aria-label="Close publish modal"
                  title="Close"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    borderRadius: '10px'
                  }}
                >
                  <FiX />
                </button>
              </header>

              <div className="cc-publish-content" style={{ padding: '32px' }}>
                <div className="cc-publish-section">
                  <div className="cc-publish-label">Social preview</div>
                  <div className="cc-social-preview" style={{ maxWidth: '100%' }}>
                    <div className="cc-social-image">
                      {socialImageUrl ? (
                        <img src={socialImageUrl} alt="" />
                      ) : (
                        <div className="cc-social-image-placeholder">No image</div>
                      )}
                      <button
                        type="button"
                        className="cc-social-edit"
                        onClick={openEditSocial}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          borderRadius: '8px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        Edit
                      </button>
                    </div>
                    <div className="cc-social-meta">
                      <div className="cc-social-title">
                        {(socialTitle || title || "Title").trim()}
                      </div>
                      <div className="cc-social-desc">
                        {(socialDescription || subtitle || "Description").trim()}
                      </div>
                      <div className="cc-social-domain">auravit</div>
                    </div>
                  </div>
                </div>
              </div>

              <footer className="cc-publish-footer" style={{ padding: '20px 32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button"
                  className="cc-publish-cancel"
                  onClick={closePublishModal}
                  disabled={isPublishing}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    opacity: isPublishing ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button type="button"
                  className="cc-publish-send"
                  onClick={submitForApproval}
                  disabled={isPublishing}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    opacity: isPublishing ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isPublishing) {
                      e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                      e.target.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isPublishing) {
                      e.target.style.background = '#f1f5f9';
                      e.target.style.color = '#475569';
                    }
                  }}
                >
                  {isPublishing ? 'Publishing...' : 'Publish'}
                </button>
              </footer>
            </div>
          </div>
        </Portal>
      )}

      {/* Edit social preview modal (separate from Publish modal) with Branding */}
      {isEditSocialOpen && (
        <Portal>
          <div className="cc-edit-social-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
            <div className="cc-edit-social-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <header className="cc-edit-social-header" style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                color: '#fff',
                padding: '24px 32px'
              }}>
                <div>
                  <div className="cc-edit-social-title" style={{ color: '#fff' }}>Edit social preview</div>
                  <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                    Change how this post appears on other platforms.
                  </div>
                </div>
                <button
                  type="button"
                  className="cc-edit-social-close"
                  onClick={closeEditSocial}
                  aria-label="Close edit social preview"
                  title="Close"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    borderRadius: '10px'
                  }}
                >
                  <FiX />
                </button>
              </header>

              <div className="cc-edit-social-body" style={{ padding: '32px' }}>
                <label className="cc-edit-field">
                  <span>Title</span>
                  <input
                    type="text"
                    value={socialTitle}
                    onChange={(e) => setSocialTitle(e.target.value)}
                    placeholder="Title"
                  />
                </label>

                <label className="cc-edit-field">
                  <span>Description</span>
                  <textarea
                    value={socialDescription}
                    onChange={(e) => setSocialDescription(e.target.value)}
                    placeholder="Description"
                    rows={4}
                    maxLength={250}
                  />
                  <div className="cc-edit-count">
                    {socialDescription.length} / 250 (Recommended)
                  </div>
                </label>

                <div className="cc-edit-image-section">
                  <div className="cc-edit-image-label">Image</div>
                  <div
                    className="cc-edit-image-box"
                    role="button"
                    tabIndex={0}
                    onClick={() => socialImageInputRef.current?.click()}
                    onKeyDown={(e) =>
                      e.key === "Enter" && socialImageInputRef.current?.click()
                    }
                    title="Click to import image"
                  >
                    {socialImageUrl ? (
                      <>
                        <img src={socialImageUrl} alt="" />
                        <button
                          type="button"
                          className="cc-edit-image-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSocialImageUrl("");
                          }}
                          aria-label="Remove image"
                          title="Remove image"
                        >
                          <FiX />
                        </button>
                      </>
                    ) : (
                      <div className="cc-edit-image-placeholder">
                        Click to import an image
                      </div>
                    )}
                    <input
                      ref={socialImageInputRef}
                      type="file"
                      accept="image/*"
                      className="cc-import-file-input"
                      onChange={handlePickSocialImage}
                    />
                  </div>
                </div>
              </div>

              <footer className="cc-edit-social-footer" style={{ padding: '20px 32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button"
                  className="cc-edit-cancel"
                  onClick={closeEditSocial}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button type="button"
                  className="cc-edit-save"
                  onClick={closeEditSocial}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }}
                >
                  Save
                </button>
              </footer>
            </div>
          </div>
        </Portal>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <Portal>
          <div className="cc-edit-social-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
            <div className="cc-edit-social-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <header className="cc-edit-social-header" style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                color: '#fff',
                padding: '24px 32px'
              }}>
                <div>
                  <div className="cc-edit-social-title" style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>Leave or Continue Revising</div>
                  <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                    You have changes unpublished
                  </div>
                </div>
                <button
                  type="button"
                  className="cc-edit-social-close"
                  onClick={handleContinueEditing}
                  aria-label="Close modal"
                  title="Close"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    borderRadius: '10px'
                  }}
                >
                  <FiX />
                </button>
              </header>

              <div className="cc-edit-social-body" style={{ padding: '32px' }}>
                <p style={{ color: '#475569', fontSize: '16px', lineHeight: '1.6' }}>
                  Your revisions will not be published and will be deleted.
                </p>
              </div>

              <footer className="cc-edit-social-footer" style={{ padding: '20px 32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button"
                  onClick={handleContinueEditing}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }}
                >
                  Continue Editing
                </button>
                <button type="button"
                  onClick={handleLeaveUnsaved}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }}
                >
                  Leave
                </button>
              </footer>
            </div>
          </div>
        </Portal>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <Portal>
          <div className="cc-success-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
            <div className="cc-success-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <header className="cc-success-header" style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                color: '#fff',
                padding: '24px 32px'
              }}>
                <div className="cc-success-icon" style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '2px solid rgba(255,255,255,0.3)'
                }}>
                  <div className="cc-success-checkmark" style={{ color: '#fff' }}>✓</div>
                </div>
                <div className="cc-success-title" style={{ color: '#fff' }}>
                  Revision Published Successfully!
                </div>
              </header>

              <div className="cc-success-content" style={{ padding: '32px' }}>
                <p style={{ color: '#475569', fontSize: '16px', lineHeight: '1.6' }}>
                  The revised content has been successfully published.
                </p>
                <p style={{ color: '#475569', fontSize: '16px', lineHeight: '1.6' }}>
                  The changes will now be visible in the content management dashboard.
                </p>
              </div>

              <footer className="cc-success-footer" style={{ padding: '20px 32px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button
                  type="button"
                  className="cc-success-btn"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(`/admin/content-review/${approvalId}`);
                  }}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }}
                >
                  Back to Content Review
                </button>
              </footer>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default AdminContentRevise;
